/**
 * features/finance/TaxVat.tsx
 * ------------------------------------------------------------------
 * VAT — SWITCHED OFF UNTIL THE SCHOOL SAYS OTHERWISE.
 *
 * Under Botswana's Value Added Tax Act the supply of EDUCATION IS AN
 * EXEMPT SUPPLY. A school billing only tuition does not register, charges
 * no output tax, and cannot reclaim the input tax it pays its suppliers —
 * that VAT is simply part of what things cost. For most schools, off is
 * not a placeholder. It is the correct answer.
 *
 * So the switch leads, and the screen explains what each position means
 * rather than presenting a tick box somebody guesses at. A school that
 * turns this on because it looked like a feature it was missing would
 * start charging tax it has no right to collect.
 *
 * TURNING IT ON IS A DECLARATION. Registration is a legal status with a
 * number, an effective date and filing obligations, so the form asks for
 * those and refuses without them.
 *
 * THE MIXED CASE IS THE LIKELY ONE. Tuition exempt, a uniform shop or
 * hall hire taxable. Then only an agreed proportion of input tax can be
 * reclaimed, and the rest is a COST — shown as such, so it never reads as
 * money the school is owed and somebody chases BURS for.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  Receipt, AlertTriangle, CheckCircle2, Info, Landmark, Ban,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { db, setDoc } from '@/lib/firebase';
import { FINANCE_COLLECTIONS } from '@/lib/financeCore';
import { useGlData } from '@/features/finance/useGlData';
import {
  VAT_OFF, BW_STANDARD_VAT_RATE, vatActive, vatSetupProblem,
  buildVatReturn, vatPeriods,
  type VatSettings, type VatBasis,
} from '@/lib/vat';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
} from '@/features/finance/ui';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SETTINGS_DOC = 'school';

export default function TaxVat() {
  const [fy] = useState(`FY${new Date().getFullYear()}`);
  const gl = useGlData(fy);

  const [stored, setStored] = useState<VatSettings | null>(null);
  const [draft, setDraft] = useState<VatSettings>(VAT_OFF);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [periodIdx, setPeriodIdx] = useState(0);

  useEffect(() => onSnapshot(
    doc(db, FINANCE_COLLECTIONS.settings, SETTINGS_DOC),
    (snap) => {
      const v = (snap.data()?.vat ?? VAT_OFF) as VatSettings;
      setStored(v);
      setDraft(v);
    },
    () => { setStored(VAT_OFF); setDraft(VAT_OFF); },
  ), []);

  const problem = vatSetupProblem(draft);
  const live = vatActive(stored);

  const periods = useMemo(
    () => (stored && vatActive(stored) ? vatPeriods(stored) : []),
    [stored],
  );
  const period = periods[periodIdx];

  const ret = useMemo(
    () => (stored && period
      ? buildVatReturn(stored, gl.journals, gl.accounts, period.from, period.to)
      : null),
    [stored, period, gl.journals, gl.accounts],
  );

  const hasAccounts = gl.accounts.some((a) => a.systemKey === 'VAT_OUTPUT')
    && gl.accounts.some((a) => a.systemKey === 'VAT_INPUT');

  async function save() {
    if (problem) return;
    setSaving(true);
    try {
      await setDoc(
        doc(db, FINANCE_COLLECTIONS.settings, SETTINGS_DOC),
        { vat: draft, updatedAt: new Date().toISOString() },
        { merge: true },
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  }

  if (stored === null || gl.loading) {
    return <div className="p-6 flex justify-center"><Spinner /></div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <PageHeader
        icon={Receipt} tone="navy"
        title="VAT"
        subtitle={live
          ? `Registered · ${stored.vatNumber} · ${stored.rate}%`
          : 'Not registered — which for most schools is the correct answer'}
      />

      {/* The explanation leads, because the wrong reason to switch this on
          is that it looked like a missing feature. */}
      <SoftCard className="mb-5 border-l-4 border-sky-400">
        <p className="text-sm text-gray-700 dark:text-gray-200 flex items-start gap-2">
          <Info size={15} className="mt-0.5 shrink-0 text-sky-600" />
          <span>
            Under Botswana&rsquo;s VAT Act the supply of <strong>education is exempt</strong>.
            A school billing only tuition does not register, charges no VAT, and
            <strong> cannot reclaim the VAT it pays its suppliers</strong> — that tax is part of
            what things cost. Turn this on only if the school is actually registered with
            BURS, which usually happens when something other than tuition is sold: a uniform
            shop, hall hire, a bus service.
          </span>
        </p>
      </SoftCard>

      <SoftCard pad="p-0" className="mb-5">
        <PanelHeader icon={Landmark} tone="navy" title="Registration"
          subtitle="A legal status, not a preference" />

        <div className="p-4 space-y-4">
          {/* The switch itself. Two named positions rather than a tick box,
              because "off" is a real answer and should read like one. */}
          <div className="flex flex-wrap gap-2">
            {([
              ['not_registered', 'Not registered', 'No VAT is charged or reclaimed'],
              ['registered', 'Registered with BURS', 'VAT is charged and returns are filed'],
            ] as const).map(([value, label, help]) => (
              <button
                key={value} type="button"
                onClick={() => setDraft({ ...draft, status: value })}
                className={`flex-1 min-w-[220px] text-left px-4 py-3 rounded-xl border-2 transition-colors ${
                  draft.status === value
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                }`}
              >
                <span className="block font-semibold text-sm text-gray-900 dark:text-white">
                  {label}
                </span>
                <span className="block text-[12px] text-gray-500 mt-0.5">{help}</span>
              </button>
            ))}
          </div>

          {draft.status === 'registered' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wide text-gray-400">
                    BURS VAT number
                  </span>
                  <input value={draft.vatNumber ?? ''}
                    onChange={(e) => setDraft({ ...draft, vatNumber: e.target.value })}
                    className={glassFieldClass('mt-1')} placeholder="C12345678901" />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wide text-gray-400">
                    Registered from
                  </span>
                  <input type="date" value={draft.registeredFrom ?? ''}
                    onChange={(e) => setDraft({ ...draft, registeredFrom: e.target.value })}
                    className={glassFieldClass('mt-1')} />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wide text-gray-400">
                    Rate (%)
                  </span>
                  <input type="number" step="0.1" value={draft.rate}
                    onChange={(e) => setDraft({ ...draft, rate: Number(e.target.value) })}
                    className={glassFieldClass('mt-1 text-right')} />
                  <span className="text-[11px] text-gray-400">
                    Botswana standard rate is {BW_STANDARD_VAT_RATE}%
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wide text-gray-400">
                    What the school supplies
                  </span>
                  <select value={draft.basis}
                    onChange={(e) => setDraft({ ...draft, basis: e.target.value as VatBasis })}
                    className={glassFieldClass('mt-1')}>
                    <option value="mixed">Mixed — tuition exempt, something else taxable</option>
                    <option value="fully_taxable">Everything taxable</option>
                  </select>
                </label>

                {draft.basis === 'mixed' && (
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wide text-gray-400">
                      Input tax recoverable (%)
                    </span>
                    <input type="number" step="1" value={draft.recoverablePct ?? ''}
                      onChange={(e) => setDraft({ ...draft, recoverablePct: Number(e.target.value) })}
                      className={glassFieldClass('mt-1 text-right')} />
                    <span className="text-[11px] text-gray-400">
                      The apportionment agreed with BURS. Input tax on exempt activity
                      cannot be reclaimed.
                    </span>
                  </label>
                )}
              </div>
            </>
          )}

          {problem && (
            <p className="text-[13px] text-red-600 flex items-start gap-1.5">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />{problem}
            </p>
          )}

          {draft.status === 'registered' && !hasAccounts && !problem && (
            <p className="text-[13px] text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              No VAT_OUTPUT and VAT_INPUT accounts are mapped in the chart of accounts yet.
              The return reads its figures off those balances, so it will show nil until
              they exist.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100 dark:border-gray-700">
          {saved && (
            <span className="text-[13px] text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
              <CheckCircle2 size={14} /> Saved
            </span>
          )}
          <button type="button" className={btnGhost} onClick={() => setDraft(stored)}>
            Reset
          </button>
          <button type="button" className={btnPrimary} onClick={save}
            disabled={saving || Boolean(problem)}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </SoftCard>

      {!live ? (
        <SoftCard>
          <p className="text-sm text-gray-500 text-center py-6 flex flex-col items-center gap-2">
            <Ban size={20} className="text-gray-300" />
            {/* Nothing computes rather than computing zero: a return of nil
                looks like a return that was filed. */}
            VAT is switched off, so there is no return to prepare. Nothing in the ledger
            is being treated as taxable.
          </p>
        </SoftCard>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">Period</span>
            <select value={periodIdx} onChange={(e) => setPeriodIdx(Number(e.target.value))}
              className={glassFieldClass('max-w-xs')}>
              {periods.map((p, i) => (
                <option key={p.from} value={i}>{p.label}</option>
              ))}
            </select>
          </div>

          {ret && (
            <>
              {ret.hasPreRegistration && (
                <SoftCard className="mb-4 border-l-4 border-amber-400">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <AlertTriangle size={15} className="inline mr-1 -mt-0.5" />
                    Some entries in this period predate the registration date. A school cannot
                    file for a period it was not registered in — check the dates before
                    submitting.
                  </p>
                </SoftCard>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                <StatChip tone="navy" value={money(ret.outputTax)} label="Output tax charged" />
                <StatChip tone="emerald" value={money(ret.inputTaxRecoverable)}
                  label="Input tax reclaimable" />
                <StatChip tone={ret.net >= 0 ? 'rose' : 'emerald'}
                  value={money(Math.abs(ret.net))}
                  label={ret.net >= 0 ? 'Payable to BURS' : 'Refund due from BURS'} />
              </div>

              <SoftCard pad="p-0">
                <PanelHeader icon={Receipt} tone="navy" title="The return"
                  subtitle="Read off the VAT account balances, so it cannot disagree with the trial balance" />
                <table className="w-full text-sm">
                  <tbody>
                    <Row label="Output tax on taxable supplies" value={ret.outputTax} />
                    <Row label="Input tax paid to suppliers" value={ret.inputTaxPaid} muted />
                    {stored.basis === 'mixed' && (
                      <Row
                        label={`Not reclaimable — relates to exempt supplies (${100 - (stored.recoverablePct ?? 0)}%)`}
                        value={ret.inputTaxIrrecoverable}
                        muted
                        note="A cost to the school, not money owed to it."
                      />
                    )}
                    <Row label="Input tax reclaimable" value={ret.inputTaxRecoverable} />
                    <tr className="border-t-2 border-gray-200 dark:border-gray-700 font-bold">
                      <td className="py-3 pl-4 pr-2">
                        {ret.net >= 0 ? 'Payable to BURS' : 'Refund due from BURS'}
                      </td>
                      <td className="py-3 pr-4 text-right tabular-nums">
                        {money(Math.abs(ret.net))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </SoftCard>
            </>
          )}
        </>
      )}
    </div>
  );
}

function Row({
  label, value, muted, note,
}: { label: string; value: number; muted?: boolean; note?: string }) {
  return (
    <tr className="border-b border-gray-50 dark:border-gray-800">
      <td className={`py-2.5 pl-4 pr-2 ${muted ? 'text-gray-500' : 'text-gray-800 dark:text-gray-100'}`}>
        {label}
        {note && <span className="block text-[11px] text-gray-400">{note}</span>}
      </td>
      <td className={`py-2.5 pr-4 text-right tabular-nums ${muted ? 'text-gray-500' : ''}`}>
        {money(value)}
      </td>
    </tr>
  );
}
